import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useBannerHeight } from "@/components/NotificationBanner";
import logoSmall from "@/assets/logo-small.webp";
import logoMobile from "@/assets/logo-mobile.webp";
import logoTiny from "@/assets/logo-tiny.webp";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useCMSPages } from "@/hooks/useCMSPages";
import { useScrollLock } from "@/hooks/useScrollLock";

const navLinks = [
  { href: "/", label: "Home", pageKey: "home" },
  { href: "/portfolio", label: "Portfolio", pageKey: "portfolio" },
  { href: "/services", label: "Services", pageKey: "services" },
  { href: "/about", label: "About", pageKey: "about" },
  { href: "/contact", label: "Contact", pageKey: "contact" },
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { settings: siteSettings } = useSiteSettings();
  const location = useLocation();
  const { user } = useAuth();
  const bannerHeight = useBannerHeight();
  const { data: cmsPages } = useCMSPages();
  useScrollLock(isMobileMenuOpen);

  const getPage = (pageKey: string) => cmsPages?.find((p) => p.page_key === pageKey);
  const isPageEnabled = (pageKey: string) => {
    const page = getPage(pageKey);
    return page ? page.is_enabled : true;
  };
  // Order follows the CMS "Pages" ordering
  const visibleLinks = navLinks
    .filter((link) => isPageEnabled(link.pageKey))
    .sort(
      (a, b) =>
        (getPage(a.pageKey)?.display_order ?? 999) -
        (getPage(b.pageKey)?.display_order ?? 999)
    );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Calculate scroll progress
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 0;
      setScrollProgress(Math.min(progress, 100));
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{ top: bannerHeight }}
        className="fixed left-0 right-0 z-50 border-b border-border bg-background/95 text-foreground shadow-sm backdrop-blur-md transition-all duration-300"
      >
        {/* Subtle gold gradient border - more visible on scroll */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent transition-opacity duration-300"
          style={{ opacity: isScrolled ? 0.6 : 0.3 }}
        />
        <div className="container mx-auto px-6">
          <nav className="flex items-center justify-between h-16">
            {/* Logo - optimized with responsive sizing */}
            <Link to="/" className="relative z-10">
              <img
                src={siteSettings.logoUrl || logoTiny}
                srcSet={siteSettings.logoUrl ? undefined : `${logoTiny} 170w, ${logoMobile} 210w, ${logoSmall} 416w`}
                sizes="(max-width: 640px) 60px, (max-width: 768px) 72px, 85px"
                alt="Himilo Studio"
                width="85"
                height="80"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="h-10 sm:h-12 md:h-14 w-auto transition-transform duration-200 hover:scale-[1.02]"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4 lg:gap-8">
              {visibleLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-xs lg:text-sm font-medium tracking-wide transition-colors hover:text-primary ${
                    location.pathname === link.href
                      ? "text-primary"
                      : "text-foreground/75"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <Button asChild variant="outline" className="ml-4 bg-transparent border-border text-foreground hover:bg-secondary hover:text-foreground">
                  <Link to="/dashboard">
                    <User size={16} className="mr-2" />
                    Dashboard
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild variant="ghost" className="ml-4 text-foreground hover:bg-secondary hover:text-foreground">
                    <Link to="/auth">Login</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/contact">Get a Quote</Link>
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden relative z-10 -mr-2 rounded-md p-3 text-foreground"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
          
          {/* Scroll Progress Bar */}
          <motion.div
            className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary via-gold to-gold/80 origin-left"
            style={{ width: `${scrollProgress}%` }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.1 }}
          />
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] md:hidden"
          >
            <div
              className="absolute inset-0 bg-foreground/40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.nav
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="absolute right-0 top-0 bottom-0 flex w-[86%] max-w-xs flex-col border-l border-border bg-background shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <img
                  src={siteSettings.logoUrl || logoTiny}
                  alt="Himilo Studio"
                  className="h-9 w-auto"
                />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label="Close menu"
                  className="-mr-2 rounded-md p-3 text-foreground hover:bg-secondary"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
                <div className="flex flex-col">
                  {visibleLinks.map((link, index) => (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + index * 0.05 }}
                    >
                      <Link
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex min-h-[52px] items-center border-b border-border/60 text-lg font-serif transition-colors hover:text-primary ${
                          location.pathname === link.href
                            ? "text-primary"
                            : "text-foreground"
                        }`}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div
                className="space-y-3 border-t border-border px-5 py-5"
                style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
              >
                {user ? (
                  <Button asChild variant="outline" className="w-full bg-transparent border-border text-foreground hover:bg-secondary hover:text-foreground">
                    <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                      <User size={16} className="mr-2" />
                      Dashboard
                    </Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="outline" className="w-full bg-transparent border-border text-foreground hover:bg-secondary hover:text-foreground">
                      <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)}>Login</Link>
                    </Button>
                    <Button asChild className="w-full">
                      <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>Get a Quote</Link>
                    </Button>
                  </>
                )}
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

